import { useEffect, useMemo, useState } from 'react';
import { listAssignmentsResult } from '@/modules/deals/services';
import { buildStaffOperationalReadModel } from '@/modules/staff/services';
import { getDashboardDealsDataResult } from '../../dashboard/services/dashboard.service';
import { listCalendarEventsResult } from '../../calendar/services/calendar.service';
import { listPaymentsResult } from '../../accounting/services/accounting.service';
import { buildStaffOverview } from '../utils/staff.overview';

const sourceStatus = result => result?.ok ? 'ready' : 'unavailable';

export function useStaffOperationalDetail(staffMember, isOpen) {
  const [state, setState] = useState({
    status: 'idle',
    errorMessage: '',
    enrichedStaffMember: staffMember || null,
    overview: {},
    dealsStatus: 'idle',
    calendarStatus: 'idle',
    paymentsStatus: 'idle',
  });

  useEffect(() => {
    let active = true;

    async function load() {
      if (!isOpen || !staffMember) {
        setState({ status: 'idle', errorMessage: '', enrichedStaffMember: staffMember || null, overview: {} });
        return;
      }

      const hasPrefetchedAssignments =
        (staffMember.activeAssignments || []).length > 0 ||
        (staffMember.completedAssignments || []).length > 0;
      setState({
        status: hasPrefetchedAssignments ? 'ready' : 'loading',
        errorMessage: '',
        enrichedStaffMember: staffMember,
        overview: {},
        dealsStatus: 'loading',
        calendarStatus: 'loading',
        paymentsStatus: 'loading',
      });
      const responses = await Promise.allSettled([
        listAssignmentsResult(), getDashboardDealsDataResult(), listCalendarEventsResult(), listPaymentsResult(),
      ]);
      if (!active) return;
      const [result, deals, calendar, payments] = responses.map(response => response.status === 'fulfilled' ? response.value : null);
      const overviewState = {
        overview: buildStaffOverview(staffMember, {
          ...(deals?.ok ? deals.data : {}),
          calendarEvents: calendar?.ok ? calendar.data : [],
          payments: payments?.ok ? payments.data : [],
        }),
        dealsStatus: sourceStatus(deals),
        calendarStatus: sourceStatus(calendar),
        paymentsStatus: sourceStatus(payments),
      };

      if (!result?.ok) {
        setState({
          ...overviewState,
          status: result?.error === 'ASSIGNMENTS_AUTH_REQUIRED' ? 'auth_required' : 'unavailable',
          errorMessage: result?.message || 'Vista operativa personale non disponibile in questo momento',
          enrichedStaffMember: staffMember,
        });
        return;
      }

      const [enrichedStaffMember] = buildStaffOperationalReadModel([staffMember], result.data || []);
      const hasAssignments =
        (enrichedStaffMember?.activeAssignments || []).length > 0 ||
        (enrichedStaffMember?.completedAssignments || []).length > 0;
      setState({
        ...overviewState,
        status: hasAssignments ? 'ready' : 'empty',
        errorMessage: '',
        enrichedStaffMember,
      });
    }

    load();

    return () => {
      active = false;
    };
  }, [staffMember, isOpen]);

  return useMemo(() => ({
    enrichedStaffMember: state.enrichedStaffMember || staffMember,
    activeAssignments: state.enrichedStaffMember?.activeAssignments || [],
    completedAssignments: state.enrichedStaffMember?.completedAssignments || [],
    status: state.status,
    errorMessage: state.errorMessage,
    ...state.overview,
    dealsStatus: state.dealsStatus,
    calendarStatus: state.calendarStatus,
    paymentsStatus: state.paymentsStatus,
  }), [staffMember, state]);
}
